import { useMemo } from 'react';
import type { CandidateProfile, ProfileStrength, ProfileStrengthSection, ProfileStrengthTier } from '@/types';

/**
 * Profile Strength widget — ring + tier chip + weighted section chips.
 *
 * Renders the server-computed score from `GET /workseeking/candidates/me/strength`
 * when available. Falls back to a local computation over the same section ids
 * and weights so the dashboard stays functional if the endpoint fails.
 *
 * @see Documentation/Specification/020_Definitions/application-specific/workseeking/ProfileStrength.md
 * @see sprints/2026-10/Milestone_09_BluePagesCandidateGamification/sprint-01-profile-strength-backend
 */
interface ProfileStrengthProps {
  /** Server result — preferred source. */
  strength?: ProfileStrength | null;
  profile: CandidateProfile | undefined;
  skillsCount?: number;
  experienceCount?: number;
  verified?: boolean;
  onSectionClick?: (section: string) => void;
}

/** Mirrors SECTIONS in profile-strength.service.ts (weights sum to 100). */
const FALLBACK_SECTIONS: Array<{
  id: string;
  label: string;
  weight: number;
  fill: (p: CandidateProfile | undefined, skills: number, exp: number, verified: boolean) => boolean;
}> = [
  { id: 'headline', label: 'Headline', weight: 5, fill: (p) => !!p?.headline && p.headline.length >= 5 },
  { id: 'bio', label: 'Bio', weight: 15, fill: (p) => !!p?.bio && p.bio.length > 10 },
  { id: 'photo', label: 'Photo', weight: 10, fill: (p) => !!p?.profileImageRef },
  { id: 'skills', label: 'Skills', weight: 15, fill: (_p, skills) => skills > 0 },
  { id: 'experience', label: 'Experience', weight: 15, fill: (_p, _s, exp) => exp > 0 },
  { id: 'locations', label: 'Locations', weight: 10, fill: (p) => (p?.desiredLocations?.length ?? 0) > 0 },
  { id: 'salary', label: 'Salary', weight: 5, fill: (p) => p?.desiredSalaryMin != null && p?.desiredSalaryMax != null },
  { id: 'industry', label: 'Industry', weight: 5, fill: (p) => !!p?.industry },
  { id: 'preferences', label: 'Preferences', weight: 10, fill: (p) => !!p?.remotePreference },
  { id: 'verified', label: 'Verified', weight: 10, fill: (_p, _s, _e, verified) => verified },
];

export function tierForScore(score: number): ProfileStrengthTier {
  if (score >= 90) return 'Gold';
  if (score >= 70) return 'Silver';
  if (score >= 40) return 'Bronze';
  return 'Starter';
}

const TIER_COLORS: Record<ProfileStrengthTier, string> = {
  Starter: 'var(--bp-text-muted, #64748b)',
  Bronze: '#d97706',
  Silver: '#94a3b8',
  Gold: '#fbbf24',
};

function computeFallback(
  profile: CandidateProfile | undefined,
  skillsCount: number,
  experienceCount: number,
  verified: boolean,
): { score: number; tier: ProfileStrengthTier; sections: ProfileStrengthSection[] } {
  const sections = FALLBACK_SECTIONS.map((s) => ({
    id: s.id,
    label: s.label,
    weight: s.weight,
    filled: s.fill(profile, skillsCount, experienceCount, verified),
    hint: '',
  }));
  const score = sections.reduce((sum, s) => sum + (s.filled ? s.weight : 0), 0);
  return { score, tier: tierForScore(score), sections };
}

export function ProfileStrength({
  strength,
  profile,
  skillsCount = 0,
  experienceCount = 0,
  verified = false,
  onSectionClick,
}: ProfileStrengthProps) {
  const computed = useMemo(() => {
    if (strength && Array.isArray(strength.sections) && strength.sections.length > 0) {
      return { score: strength.score, tier: strength.tier, sections: strength.sections };
    }
    return computeFallback(profile, skillsCount, experienceCount, verified);
  }, [strength, profile, skillsCount, experienceCount, verified]);

  const { score, tier, sections } = computed;
  const pct = Math.round(score);

  const size = 80;
  const stroke = 6;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (circumference * pct) / 100;

  const color = pct >= 80 ? 'var(--bp-success, #22c55e)' : pct >= 50 ? 'var(--bp-warning, #f59e0b)' : 'var(--bp-error, #ef4444)';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0' }}>
      <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg viewBox={`0 0 ${size} ${size}`} style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke={color} strokeLinecap="round"
            strokeWidth={stroke}
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            style={{ transition: 'stroke-dashoffset 0.6s ease-out' }}
          />
        </svg>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 700, color }}>{pct}%</span>
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Profile strength</span>
          <span
            data-testid="strength-tier"
            style={{
              fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em',
              textTransform: 'uppercase', color: '#0b1220',
              background: TIER_COLORS[tier], borderRadius: 10, padding: '2px 8px',
            }}
          >
            {tier}
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              title={s.hint || undefined}
              onClick={() => onSectionClick?.(s.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: '0.7rem', padding: '2px 8px', borderRadius: 12,
                background: s.filled ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.05)',
                color: s.filled ? 'var(--bp-success, #22c55e)' : 'var(--bp-text-muted, #64748b)',
                border: 'none', cursor: onSectionClick ? 'pointer' : 'default',
                textDecoration: 'none',
              }}
            >
              {s.filled ? '✓' : '○'} {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
